import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Drawer,
  Empty,
  Segmented,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  BillingActivityRow,
  BillingClassRow,
  BillingDrillData,
  fmtINR,
  fmtINRCompact,
  fmtMonthLong,
  fmtQty,
  num,
} from "./executionShared";

// Chainage prints trimmed (101.000 → 101, 102.760 → 102.76) — the survey
// precision is stored, not paraded.
const fmtCh = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) ? String(parseFloat(n.toFixed(3))) : value;
};

// Small stat card in the drill's KPI row (qty flavor of the month-detail
// statChip — those format money, these format driver quantities).
const kpiCard = (label: string, value: React.ReactNode, tooltip?: string) => {
  const card = (
    <div
      key={label}
      style={{
        background: "#f7f9fc",
        border: "1px solid #e3e9f3",
        borderRadius: 8,
        padding: "6px 14px",
        minWidth: 130,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#8094ae" }}>{label}</div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: "#022c57",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
    </div>
  );
  return tooltip ? (
    <Tooltip title={tooltip} key={label}>
      {card}
    </Tooltip>
  ) : (
    card
  );
};

// Actual Client Billing drill for one month: activity-wise running meters (the
// site-entered driver quantities) with the billing they exploded into.
//  - click the ACTIVITY row expander → task-wise breakdown (qty × Basic Rate)
//  - click the RUNNING METER value → Drawer with the class-wise (rock-class)
//    breakdown incl. chainage stretches, plus the date-wise inputs view
// Activities billed via MANUAL task actuals have no daily inputs — their
// running-meter cell renders a muted em-dash and a "manual" tag.

const BillingDrillTab: React.FC<{
  projectId: number | string;
  month: string;
  phaseId?: number;
  activityId?: number;
}> = ({ projectId, month, phaseId, activityId }) => {
  const wbsName = useWbsName();
  const [data, setData] = useState<BillingDrillData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dayDrill, setDayDrill] = useState<BillingActivityRow | null>(null);
  // 'class' = class-wise (rock-class) breakdown with chainage stretches;
  // 'date' = the flat date-wise inputs + billing. Chosen per activity on open.
  const [drillView, setDrillView] = useState<"class" | "date">("class");
  const [exporting, setExporting] = useState(false);
  const currentUser = useSelector((state: any) => state.currentUser?.data);

  useEffect(() => {
    if (!projectId || !month) return;
    let cancelled = false;
    setLoading(true);
    threeMReportApis
      .getMonthlyExecutionBillingDrill(projectId, month, { phaseId, activityId })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) setData(response?.data || {});
        else message.error(response?.message || "Failed to load billing drill");
      })
      .catch(() => !cancelled && message.error("Failed to load billing drill"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, activityId]);

  const activities = useMemo(() => data?.activities || [], [data]);

  // Running-meter KPIs off the daily driver inputs. Days are the UNION of
  // capture dates across activities (two activities advancing on the same day
  // is one working day, not two). Driver UOMs can differ per activity — with
  // mixed units the totals still add, but the cards say so.
  const runningMeterKpis = useMemo(() => {
    const withDriver = activities.filter((a) => a.driverQty !== "");
    const totalQty = withDriver.reduce((sum, a) => sum + num(a.driverQty), 0);
    const dates = new Set<string>();
    withDriver.forEach((a) => a.days.forEach((d) => dates.add(d.date)));
    // Array.from, not a spread — this repo's TS target can't spread iterators.
    const uoms = new Set(withDriver.map((a) => a.driverUom).filter(Boolean));
    const uom = uoms.size === 1 ? Array.from(uoms)[0] : "";
    // Per-unit cost divides driver-backed billing by driver qty — activities
    // billed via MANUAL actuals have no running meters and are excluded from
    // BOTH sides, or the rate would inflate.
    const driverAmount = withDriver.reduce((sum, a) => sum + num(a.amount), 0);
    const manualOnlyCount = activities.length - withDriver.length;
    return {
      hasDriver: withDriver.length > 0,
      totalQty,
      dayCount: dates.size,
      avgPerDay: dates.size > 0 ? totalQty / dates.size : null,
      perUnitCost: totalQty > 0 ? driverAmount / totalQty : null,
      manualOnlyCount,
      uom,
      mixedUoms: uoms.size > 1,
    };
  }, [activities]);

  const handleExport = async () => {
    if (!data || activities.length === 0) return;
    setExporting(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(currentUser?.orgLogo);
      const { default: exportBillingWorkbook } = await import("./exportBillingWorkbook");
      await exportBillingWorkbook({
        // Ship display-cleaned activity names — the WBS-code split is a hook,
        // so it can't run inside the lazy-loaded workbook builder.
        activities: activities.map((a) => ({
          ...a,
          activityName: wbsName(a.activityName) || `Activity ${a.activityId}`,
        })),
        month,
        monthTotal: data.monthTotal || "0",
        projectName: data.projectName,
        orgName: currentUser?.orgName,
        logo,
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Actual Client Billing:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const taskColumns: any[] = [
    {
      title: "Task",
      key: "task",
      ellipsis: true,
      // Description first; the task code rides underneath, muted — the code is
      // reference metadata, not what the eye scans the list by. Long
      // descriptions clip to the column width and surface fully in a tooltip
      // (antd only shows it when the text actually truncated).
      render: (_: any, row: any) => (
        <div style={{ lineHeight: 1.3 }}>
          <Typography.Text
            style={{ color: "#1f3c66", fontWeight: 500, display: "block" }}
            ellipsis={{ tooltip: row.taskName }}
          >
            {row.taskName}
          </Typography.Text>
          <div
            style={{
              color: "#8094ae",
              fontSize: 11.5,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {row.taskSequenceId}
          </div>
        </div>
      ),
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 110,
      align: "right" as const,
      render: (value: string, row: any) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(value)} {row.uomName}
        </span>
      ),
    },
    {
      title: "Basic Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      align: "right" as const,
      render: (value: string) => (
        <Tooltip title={fmtINR(value)}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINRCompact(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      align: "right" as const,
      render: (value: string) => (
        <Tooltip title={fmtINR(value)}>
          <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
            {fmtINRCompact(value)}
          </b>
        </Tooltip>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 90,
      render: (value: string) => (
        <Tag color={value === "MANUAL" ? "gold" : "blue"} style={{ marginRight: 0 }}>
          {value === "MANUAL" ? "Manual" : "Daily exec"}
        </Tag>
      ),
    },
  ];

  const activityColumns: any[] = [
    {
      title: "Activity",
      key: "activity",
      ellipsis: true,
      render: (_: any, row: BillingActivityRow) => (
        <span style={{ fontWeight: 600, color: "#022c57" }}>
          {wbsName(row.activityName) || `Activity ${row.activityId}`}{" "}
          <span style={{ color: "#8094ae", fontWeight: 400 }}>
            ({row.tasks.length} tasks)
          </span>
          {row.hasManual && (
            <Tooltip title="Some of this activity's billing comes from manually entered monthly actuals, not daily execution">
              <Tag color="gold" style={{ marginLeft: 8 }}>
                manual
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Running Meter",
      key: "driverQty",
      width: 150,
      align: "right" as const,
      render: (_: any, row: BillingActivityRow) => {
        if (row.driverQty === "" || row.days.length === 0) {
          return (
            <Tooltip title="No daily-execution inputs for this activity this month (manually entered actuals)">
              <span style={{ color: "#c9d2e3" }}>—</span>
            </Tooltip>
          );
        }
        const classCount = row.classes?.length ?? 0;
        return (
          <Tooltip
            title={`${fmtQty(row.driverQty)} ${row.driverUom || ""} across ${row.days.length} day(s)${
              classCount > 0
                ? ` in ${classCount} class${classCount === 1 ? "" : "es"} — click for the class-wise breakdown`
                : " — click for the date-wise inputs"
            }`}
          >
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => {
                setDrillView(classCount > 0 ? "class" : "date");
                setDayDrill(row);
              }}
            >
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                {fmtQty(row.driverQty)} {row.driverUom}
              </span>
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right" as const,
      render: (value: string) => (
        <Tooltip title={fmtINR(value)}>
          <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
            {fmtINRCompact(value)}
          </b>
        </Tooltip>
      ),
    },
    {
      title: "₹ / unit (driver)",
      dataIndex: "perUnit",
      key: "perUnit",
      width: 140,
      align: "right" as const,
      render: (value: string) =>
        value === "" ? (
          <span style={{ color: "#c9d2e3" }}>—</span>
        ) : (
          <Tooltip title={`${fmtINR(value)} per driver unit (amount ÷ running meter)`}>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINRCompact(value)}</span>
          </Tooltip>
        ),
    },
  ];

  // Class-wise view: one row per capture class (rock class), share bar against
  // the activity's total running meter; expand for the day-wise inputs with
  // their chainage stretches.
  const drillTotalQty = num(dayDrill?.driverQty || 0);
  const classColumns: any[] = [
    {
      title: "Class",
      key: "class",
      render: (_: any, row: BillingClassRow) => {
        const share = drillTotalQty > 0 ? (num(row.qty) / drillTotalQty) * 100 : 0;
        return (
          <div style={{ lineHeight: 1.35 }}>
            <span style={{ fontWeight: 600, color: "#022c57" }}>{row.slotLabel}</span>
            <span style={{ color: "#8094ae", fontSize: 11.5, marginLeft: 6 }}>
              {row.days.length} day{row.days.length === 1 ? "" : "s"}
            </span>
            <div
              style={{
                marginTop: 4,
                height: 4,
                borderRadius: 2,
                background: "#eef1f6",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, share)}%`,
                  height: "100%",
                  borderRadius: 2,
                  background: "#2F7FDF",
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 110,
      align: "right" as const,
      render: (value: string, row: BillingClassRow) => (
        <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
          {fmtQty(value)} {row.uom}
        </b>
      ),
    },
    {
      title: "Share",
      key: "share",
      width: 80,
      align: "right" as const,
      render: (_: any, row: BillingClassRow) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#8094ae" }}>
          {drillTotalQty > 0 ? `${((num(row.qty) / drillTotalQty) * 100).toFixed(1)}%` : "—"}
        </span>
      ),
    },
  ];

  const classDayColumns: any[] = [
    { title: "Date", dataIndex: "date", key: "date", width: 100 },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 90,
      align: "right" as const,
      render: (value: string) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(value)}</span>
      ),
    },
    {
      title: "Chainage",
      dataIndex: "stretches",
      key: "stretches",
      render: (stretches: BillingClassRow["days"][number]["stretches"]) =>
        stretches.length === 0 ? (
          <Tooltip title="Captured as a typed quantity — no chainage stretches recorded">
            <span style={{ color: "#c9d2e3" }}>—</span>
          </Tooltip>
        ) : (
          <div style={{ lineHeight: 1.5 }}>
            {stretches.map((s, i) => (
              <div key={i} style={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                CH {fmtCh(s.fromChainage)} → {fmtCh(s.toChainage)}
                <span style={{ color: "#8094ae" }}> · {fmtQty(s.length)} m</span>
              </div>
            ))}
          </div>
        ),
    },
  ];

  const dayColumns: any[] = [
    { title: "Date", dataIndex: "date", key: "date", width: 120 },
    {
      title: "Driver Qty",
      dataIndex: "driverQty",
      key: "driverQty",
      align: "right" as const,
      render: (value: string) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(value)} {dayDrill?.driverUom}
        </span>
      ),
    },
    {
      title: "Billing",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (value: string) => (
        <Tooltip title={fmtINR(value)}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINRCompact(value)}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <p style={{ color: "#8c8c8c", fontSize: 12, margin: 0 }}>
          Activity-wise billing for {fmtMonthLong(month)}: Daily Execution quantities ×
          Basic Rate. Expand an activity for its task-wise breakdown; click a running
          meter for the class-wise breakdown with chainage stretches.
          {data?.monthTotal !== undefined && (
            <>
              {" "}
              Month total:{" "}
              <b style={{ color: "#022c57" }}>{fmtINR(num(data.monthTotal))}</b>
            </>
          )}
        </p>
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          disabled={loading || activities.length === 0}
          onClick={handleExport}
        >
          Export Excel
        </Button>
      </div>
      {runningMeterKpis.hasDriver && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
          {kpiCard(
            "Working Days",
            String(runningMeterKpis.dayCount),
            "Distinct dates with a daily-execution capture (any activity)."
          )}
          {kpiCard(
            "Avg Running Meter / Day",
            runningMeterKpis.avgPerDay === null
              ? "—"
              : `${fmtQty(runningMeterKpis.avgPerDay)} ${runningMeterKpis.uom}`.trim(),
            runningMeterKpis.mixedUoms
              ? "Total driver quantity ÷ working days. Activities use different driver units this month, so the figure adds unlike units."
              : "Total driver quantity ÷ working days with daily-execution captures this month."
          )}
          {kpiCard(
            "Total Running Meter",
            `${fmtQty(runningMeterKpis.totalQty)} ${runningMeterKpis.uom}`.trim(),
            runningMeterKpis.mixedUoms
              ? "Σ site-entered driver quantities across activities — units differ per activity this month."
              : "Σ site-entered driver quantities across activities this month."
          )}
          {kpiCard(
            "Per Unit Cost",
            runningMeterKpis.perUnitCost === null
              ? "—"
              : `${fmtINRCompact(runningMeterKpis.perUnitCost)}${
                  runningMeterKpis.uom ? ` / ${runningMeterKpis.uom}` : ""
                }`,
            `${
              runningMeterKpis.perUnitCost === null
                ? ""
                : `${fmtINR(runningMeterKpis.perUnitCost)} — `
            }billing of driver-backed activities ÷ total running meter${
              runningMeterKpis.manualOnlyCount > 0
                ? ` (${runningMeterKpis.manualOnlyCount} manual-only activit${
                    runningMeterKpis.manualOnlyCount === 1 ? "y" : "ies"
                  } excluded from both sides)`
                : ""
            }${runningMeterKpis.mixedUoms ? ". Driver units differ per activity this month." : "."}`
          )}
        </div>
      )}
      {!loading && activities.length === 0 ? (
        <Empty
          description="No billed actuals for this month yet — capture daily execution or monthly task actuals first"
          style={{ padding: "32px 0" }}
        />
      ) : (
        <Table
          size="small"
          rowKey="activityId"
          columns={activityColumns}
          dataSource={activities}
          pagination={false}
          scroll={{ x: 900 }}
          expandable={{
            expandedRowRender: (activity: BillingActivityRow) => (
              <Table
                size="small"
                rowKey="taskId"
                columns={taskColumns}
                dataSource={activity.tasks}
                pagination={activity.tasks.length > 25 ? { pageSize: 25 } : false}
              />
            ),
          }}
        />
      )}
      <Drawer
        title={
          dayDrill
            ? `${wbsName(dayDrill.activityName) || `Activity ${dayDrill.activityId}`} — running meter, ${fmtMonthLong(month)}`
            : ""
        }
        open={!!dayDrill}
        onClose={() => setDayDrill(null)}
        width={560}
      >
        {dayDrill && (
          // Keyed by activity: class rows reuse slotKeys across activities, so
          // an unkeyed table would carry one activity's expanded rows into the
          // next one's drawer.
          <div key={dayDrill.activityId}>
            {(dayDrill.classes?.length ?? 0) > 0 && (
              <Segmented
                size="small"
                style={{ marginBottom: 12 }}
                value={drillView}
                onChange={(value) => setDrillView(value as "class" | "date")}
                options={[
                  { label: "By class", value: "class" },
                  { label: "By date", value: "date" },
                ]}
              />
            )}
            {drillView === "class" && (dayDrill.classes?.length ?? 0) > 0 ? (
              <>
                <Table
                  size="small"
                  rowKey="slotKey"
                  columns={classColumns}
                  dataSource={dayDrill.classes}
                  pagination={false}
                  expandable={{
                    defaultExpandedRowKeys:
                      dayDrill.classes!.length === 1
                        ? [dayDrill.classes![0].slotKey]
                        : [],
                    expandedRowRender: (cls: BillingClassRow) => (
                      <Table
                        size="small"
                        rowKey="date"
                        columns={classDayColumns}
                        dataSource={cls.days}
                        pagination={false}
                      />
                    ),
                  }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <b>Total</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <b style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmtQty(dayDrill.driverQty)} {dayDrill.driverUom}
                        </b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right" />
                    </Table.Summary.Row>
                  )}
                />
                <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                  Class-wise split of the site-entered driver quantities. Expand a
                  class for its day-wise inputs with the recorded chainage
                  stretches; days captured before chainage tracking (or typed
                  without stretches) show no chainage.
                </p>
              </>
            ) : (
              <>
                <Table
                  size="small"
                  rowKey="date"
                  columns={dayColumns}
                  dataSource={dayDrill.days}
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <b>Total</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <b style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmtQty(dayDrill.driverQty)} {dayDrill.driverUom}
                        </b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <b style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmtINRCompact(dayDrill.amount)}
                        </b>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
                <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                  Driver quantities are the site-entered daily inputs; billing is the
                  exploded task value that day (Σ task qty × Basic Rate).
                </p>
              </>
            )}
          </div>
        )}
      </Drawer>
    </Spin>
  );
};

export default BillingDrillTab;
